import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'components/UnvUI'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './ForeignStudentsPage.module.scss'

const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return '—'
  }
}

const emptyForm = {
  full_name: '',
  citizenship: '',
  visa_number: '',
  visa_until: '',
  migration_card: '',
  has_registration: false,
  registration_until: '',
}

const ForeignStudentsPage = () => {
  const { t } = useTranslation('common')
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/students/foreign/')
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openEdit = (row) => {
    setEditing(row)
    setForm({
      full_name: row.full_name || '',
      citizenship: row.citizenship || '',
      visa_number: row.visa_number || '',
      visa_until: row.visa_until || '',
      migration_card: row.migration_card || '',
      has_registration: !!row.has_registration,
      registration_until: row.registration_until || '',
    })
  }

  const save = async () => {
    if (!editing) return
    try {
      await $api.patch(`/students/foreign/${editing.id}/`, form)
      setNotify({ isOpen: true, message: t('foreign.saved'), type: 'success', sound: 'success' })
      setEditing(null)
      load()
    } catch {
      setNotify({ isOpen: true, message: t('foreign.save_error'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('foreign.title_page')}</h1>
      </div>

      <div className={`unv-card ${styles.content}`}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('foreign.section_in_dev')}</div>
        ) : list.length === 0 ? (
          <div className="unv-empty">{t('foreign.no_records')}</div>
        ) : (
          <table className="unv-table">
            <thead>
              <tr>
                <th>{t('foreign.col_full_name')}</th>
                <th>{t('foreign.col_citizenship')}</th>
                <th>{t('foreign.col_visa_no')}</th>
                <th>{t('foreign.col_visa_until')}</th>
                <th>{t('foreign.col_migration_card')}</th>
                <th>{t('foreign.col_registration')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td>{r.full_name || '—'}</td>
                  <td>{r.citizenship || '—'}</td>
                  <td>{r.visa_number || '—'}</td>
                  <td>{fmtDate(r.visa_until)}</td>
                  <td>{r.migration_card || '—'}</td>
                  <td>
                    {r.has_registration ? (
                      <span className="unv-badge unv-badge--success">
                        {t('foreign.registration_yes_until', { date: fmtDate(r.registration_until) })}
                      </span>
                    ) : (
                      <span className="unv-badge unv-badge--danger">{t('foreign.registration_no')}</span>
                    )}
                  </td>
                  <td>
                    <button className="unv-btn unv-btn--ghost unv-btn--sm" onClick={() => openEdit(r)}>
                      {t('foreign.edit_btn')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('foreign.modal_edit_data')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('foreign.field_full_name')}
            value={form.full_name}
            onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('foreign.field_citizenship')}
            value={form.citizenship}
            onChange={(e) => setForm((f) => ({ ...f, citizenship: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('foreign.field_visa_no')}
            value={form.visa_number}
            onChange={(e) => setForm((f) => ({ ...f, visa_number: e.target.value }))}
            margin="normal"
          />
          <TextField
            type="date"
            fullWidth
            label={t('foreign.field_visa_until')}
            InputLabelProps={{ shrink: true }}
            value={form.visa_until}
            onChange={(e) => setForm((f) => ({ ...f, visa_until: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('foreign.field_migration_card')}
            value={form.migration_card}
            onChange={(e) => setForm((f) => ({ ...f, migration_card: e.target.value }))}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.has_registration}
                onChange={(e) => setForm((f) => ({ ...f, has_registration: e.target.checked }))}
              />
            }
            label={t('foreign.field_has_registration')}
          />
          <TextField
            type="date"
            fullWidth
            label={t('foreign.field_registration_until')}
            InputLabelProps={{ shrink: true }}
            value={form.registration_until}
            onChange={(e) => setForm((f) => ({ ...f, registration_until: e.target.value }))}
            margin="normal"
            disabled={!form.has_registration}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={save}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default ForeignStudentsPage
